/* prettier-ignore */

import { Component, computed, ElementRef, inject, QueryList, signal, ViewChild, ViewChildren, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CdkDrag, CdkDropList, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { CardTimeLineComponent } from './components/card/card-timeline.component';
import { NewCustomRouteService } from '../../new-custom-route.service';
import { PRIMARY_TIMELINE_COLORS, TIME_STRINGS } from '../../constants';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToolsService } from 'src/app/services/tools.service';
import { Subscription } from 'rxjs';
import { CdkScrollable } from '@angular/cdk/scrolling';

@Component({
  selector: 'map-timeline',
  imports: [
    CommonModule,
    DragDropModule,
    TranslateModule,
    CardTimeLineComponent
  ],
  standalone: true,
  templateUrl: './timeline.component.html',
  styleUrls: ['./timeline.component.scss'],
  encapsulation: ViewEncapsulation.None,
})

export class TimelineComponent {
    @ViewChildren("dropList") dropList: QueryList<CdkDropList>;
    @ViewChild('timelineRef', { static: true }) timelineRef!: ElementRef<HTMLElement>;
    @ViewChild(CdkScrollable) scrollable!: CdkScrollable;

    public totalAvailableHours: string[] = TIME_STRINGS;
    private routeService = inject(NewCustomRouteService);
    public translate = inject(TranslateService);
    public tools = inject(ToolsService);
    public canBeDraged = signal(false);

    public route = computed(() => this.routeService.route())
    public disableTouch = computed(() => this.routeService.disableTouch());
    public delay = computed(() => this.tools.isWebApp() ? 2000 : 150)

    private preventTouchMove = (e: TouchEvent) => e.preventDefault();
    private edgeScrollRAF: number | null = null;
    private scrollTopWhenLocked = 0;
    private pointerY = 0;
    private dragging = false;
    private timeout: any;
    private scrollSub!: Subscription;

    public readonly PRIMARY_TIMELINE_COLORS = PRIMARY_TIMELINE_COLORS;
    private readonly NO_SPACE_CLASS = 'timeline-card__no-space';
    private readonly FREE_SPACE_CLASS = 'timeline-card__free-space';
    private readonly EDGE_PX = 60;
    private readonly MAX_SPEED = 18;
    private readonly MIN_SPEED = 2;
  
    ngAfterViewInit(){
      if (!this.tools.isWebApp())
        return;
      
      this.scrollSub = this.scrollable.elementScrolled().subscribe(() => {
        clearTimeout(this.timeout);
      });
    }

    drop(event: any, day){
      if (!this.canBeDraged()){
        moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
        this.routeService.moveItemInSameDay(day);
        this.routeService.setDay(day)
      }
      this.routeService.checkRightTimes();
      this.routeService.reloadMap();
    }

    onCardDrop(event: any, item: any, listIndex: any) {
      if (!this.canBeDraged()) {
        this.resetTimelineCardStyles(); 
        return;
      };     

      const targetElement = document.elementFromPoint(event.dropPoint.x,event.dropPoint.y)?.closest('.card-item') as HTMLDivElement;
      if (!targetElement) 
        return;

      const itemId = targetElement.getAttribute('index')
      const listId = targetElement.parentElement.getAttribute('index')

      this.routeService.removeItem(item.point);
      this.routeService.moveItem(listId, itemId, item);
      this.routeService.setDay(parseInt(listId))
    }

    allowEnter(drag: CdkDrag, drop: CdkDropList): boolean {
      return false;
    }

    onDragMove(event: any, item: any): void {
      this.pointerY = event.pointerPosition.y;
      this.canBeDraged.set(false);
      this.setPreviewStyle(0.8);
      this.resetTimelineCardStyles();
    
      const targetElement = this.getTargetElement(event.pointerPosition);
      if (!targetElement) return;
    
      const { cardItem, parentIndex, itemIndex } = this.getTargetIndices(targetElement);
      const currentItemIndex = this.getCurrentItemIndex(event);
      if (parentIndex === currentItemIndex) return;
    
      const foundItem = this.route()?.[parseInt(parentIndex)]?.points?.[parseInt(itemIndex)];
      if (this.isValidDirectMove(foundItem, item)) {
        targetElement.classList.add(this.FREE_SPACE_CLASS);
        this.canBeDraged.set(true);
      } 
      else if (foundItem?.type === 'add') {
        this.handleAddMove(cardItem, parentIndex, itemIndex, item);
      } 
      else {
        targetElement.classList.add(this.NO_SPACE_CLASS);
        this.canBeDraged.set(false);
      }
    }
    
    private setPreviewStyle(opacity: number): void {
      const preview = document.querySelector('.cdk-drag-preview') as HTMLElement;
      if (preview) {
        preview.style.opacity = `${opacity}`;
        const currentTransform = preview.style.transform;
        const translateMatch = currentTransform.match(/translate3d\([^)]+\)/);
        const translatePart = translateMatch ? translateMatch[0] : '';
        preview.style.transform = `${translatePart} scale(1.05) rotate(5deg)`;
        const time = preview.querySelector('.time') as HTMLElement;
        if (time)
          time.style.display = 'none';

        const removeIcon = preview.querySelector('.remove-icon') as HTMLElement;
        if (removeIcon)
          removeIcon.style.display = 'none';
      }
    }
    
    private getTargetElement(pointerPosition: { x: number; y: number }): HTMLElement | null {
      return document.elementFromPoint(pointerPosition.x, pointerPosition.y)?.closest('.card-item')?.querySelector('.timeline-card') as HTMLDivElement || null;
    }
    
    private getTargetIndices(targetElement: HTMLElement): { cardItem: HTMLElement; parentIndex: string; itemIndex: string } {
      const cardItem = targetElement.closest('.card-item') as HTMLElement;
      const parentElement = cardItem.parentElement as HTMLElement;
      const parentIndex = parentElement.getAttribute('index') || '';
      const itemIndex = cardItem.getAttribute('index') || '';
      return { cardItem, parentIndex, itemIndex };
    }
    
    private getCurrentItemIndex(event: any): string {
      return event.source.dropContainer.element.nativeElement.getAttribute('index') || '';
    }
    
    private isValidDirectMove(foundItem: any, item: any): boolean {
      return foundItem?.point?.size >= item.point?.size && foundItem.type !== 'add';
    }
    
    private handleAddMove(cardItem: HTMLElement, parentIndex: string, itemIndex: string, item: any): void {
      this.resetTimelineCardStyles();
      const size = parseInt(item.point?.size || '0', 10);
      const arrayAdds: HTMLElement[] = [];
      let currentItem = cardItem;
      let canBeMoved = true;
    
      for (let i = parseInt(itemIndex, 10); i < parseInt(itemIndex, 10) + size; i++) {
        arrayAdds.push(currentItem);
        currentItem = currentItem?.nextElementSibling as HTMLElement;
        if (this.route()?.[parseInt(parentIndex, 10)]?.points?.[i]?.type !== 'add') {
          canBeMoved = false;
          break;
        }
      }
    
      if (canBeMoved) {
        arrayAdds.forEach((element) => {
          const test = element.querySelector('.timeline-card');
          if (test) test.classList.add('timeline-card__free-space');
        });
        this.canBeDraged.set(true);
      } else {
        cardItem.querySelector('.timeline-card')?.classList.add('timeline-card__no-space');
        this.canBeDraged.set(false);
      }
    }

    onDragReleased(event: any, item: any){
      if (!this.canBeDraged()) {
        this.resetTimelineCardStyles();
        return
      };

      const placeholder = document.querySelector(".cdk-drag-placeholder") as HTMLElement;
      if (placeholder) placeholder.style.cssText = 'animation: none !important; ';
      placeholder?.remove()
      
      const preview = document.querySelector(".cdk-drag-preview") as HTMLElement;
      if (preview) preview.style.cssText = 'display: none !important; animation: none !important;';
      preview?.remove()
    }

    private resetTimelineCardStyles(): void {
      const allItems = document.querySelectorAll('.timeline-card');
      allItems.forEach((item: HTMLDivElement) => {
        item.classList.remove(this.NO_SPACE_CLASS, this.FREE_SPACE_CLASS);
      });
    }

    addItem(dayIndex, pointIndex){
      this.routeService.modalAddItem(dayIndex, pointIndex);
    }

    onDragStarted() {
      if (!this.tools.isWebApp())
        return;

      clearTimeout(this.timeout)
      this.dragging = true;
      this.lockBodyScroll();

      const el = this.timelineRef.nativeElement;
      el.style.touchAction = 'none';
      el.style.overflow = 'hidden';
      el.addEventListener('touchmove', this.preventTouchMove, { passive: false });

      this.startEdgeAutoScroll();
    }

    onDragEnded() {
      if (!this.tools.isWebApp())
        return;

      this.dragging = false;
      this.stopEdgeAutoScroll();
      this.unlockBodyScroll();
      this.resetTimelineCardStyles();

      const el = this.timelineRef.nativeElement;
      el.style.touchAction = 'auto';
      el.style.overflow = 'scroll';
      el.removeEventListener('touchmove', this.preventTouchMove);
    }

    private startEdgeAutoScroll() {
      const loop = () => {
        if (!this.dragging) 
          return;

        const container = this.timelineRef.nativeElement;
        const rect = container.getBoundingClientRect(); 

        const distTop = this.pointerY - rect.top;
        const distBottom = rect.bottom - this.pointerY;

        let dy = 0;
        if (distTop >= 0 && distTop < this.EDGE_PX) {
          const t = 1 - distTop / this.EDGE_PX; // 0..1
          dy = -this.lerp(this.MIN_SPEED, this.MAX_SPEED, t);
        }
        else if (distBottom >= 0 && distBottom < this.EDGE_PX) {
          const t = 1 - distBottom / this.EDGE_PX; // 0..1
          dy = this.lerp(this.MIN_SPEED, this.MAX_SPEED, t);
        }

        if (dy !== 0) {
          const prev = container.scrollTop;
          container.scrollTop = prev + dy;
        }

        this.edgeScrollRAF = requestAnimationFrame(loop);
      };

      if (this.edgeScrollRAF == null) 
        this.edgeScrollRAF = requestAnimationFrame(loop);
    }

    private stopEdgeAutoScroll() {
      if (this.edgeScrollRAF != null) {
        cancelAnimationFrame(this.edgeScrollRAF);
        this.edgeScrollRAF = null;
      }
    }

    private lerp(a: number, b: number, t: number) {
      return a + (b - a) * Math.max(0, Math.min(1, t));
    }

    private lockBodyScroll() {
      this.scrollTopWhenLocked = window.scrollY || document.documentElement.scrollTop || 0;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.scrollTopWhenLocked}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
      document.documentElement.classList.add('dragging');
      document.body.classList.add('dragging');
    }

    private unlockBodyScroll() {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      document.documentElement.classList.remove('dragging');
      document.body.classList.remove('dragging');
      window.scrollTo(0, this.scrollTopWhenLocked);
    }

    onPointerDown(event){
      if (!this.tools.isWebApp())
        return;

      clearTimeout(this.timeout)
      this.timeout = setTimeout(() => {
          this.onDragStarted()
      }, 1750);
    }

    onPointerUp(event){
      if (!this.tools.isWebApp())
        return;
      
      clearTimeout(this.timeout)
      this.onDragEnded();
    }
}